import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/icons.js';
import { StatusPill, TechAvatar, fmtTime, fmtDate, fmtMoney } from '../components/shell.js';
import { clients, invoices, jobs, parts, techs, NOW, type Job } from '../data/seed.js';

function Sparkline({
  data,
  positive = true,
}: {
  data: number[];
  positive?: boolean;
}) {
  const w = 100;
  const h = 28;
  const pad = 2;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (v - min) / range) * (h - pad * 2);
    return [x, y] as const;
  });
  const d = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
  const fill =
    d +
    ` L${pts[pts.length - 1]![0].toFixed(1)},${h} L${pts[0]![0].toFixed(1)},${h} Z`;
  const stroke = positive ? 'hsl(var(--primary))' : 'hsl(var(--destructive))';
  return (
    <svg className="kpi-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <path d={fill} fill={stroke} fillOpacity="0.1" />
      <path
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function KPI({
  label,
  value,
  delta,
  deltaPositive,
  spark,
}: {
  label: string;
  value: string | number;
  delta: string;
  deltaPositive: boolean;
  spark: number[];
}) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-trend">
        <span
          className={deltaPositive ? 'up' : 'down'}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
        >
          {deltaPositive ? Icon.arrowUp({ size: 11 }) : Icon.arrowDown({ size: 11 })}
          {delta}
        </span>
        <span>vs last week</span>
      </div>
      <Sparkline data={spark} positive={deltaPositive} />
    </div>
  );
}

function MiniWeek({ jobs }: { jobs: Job[] }) {
  const navigate = useNavigate();
  const today = new Date(NOW);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return d;
  });
  const counts = days.map(
    (d) =>
      jobs.filter((j) => {
        const jd = new Date(j.scheduledStart);
        return jd.toDateString() === d.toDateString() && j.status !== 'Cancelled';
      }).length,
  );
  const max = Math.max(...counts, 1);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
      {days.map((d, i) => {
        const isToday = i === 0;
        return (
          <button
            key={i}
            onClick={() => navigate('/calendar')}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '6px 4px',
              borderRadius: 7,
              textAlign: 'center',
              color: 'hsl(var(--foreground))',
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = 'hsl(var(--muted))')}
            onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <div
              style={{
                fontSize: 10,
                color: 'hsl(var(--muted-foreground))',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2)}
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: isToday ? 'hsl(var(--primary))' : 'hsl(var(--foreground))',
                marginTop: 2,
              }}
            >
              {d.getDate()}
            </div>
            <div
              style={{
                marginTop: 6,
                height: 28,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  width: '60%',
                  height: `${(counts[i]! / max) * 100}%`,
                  minHeight: counts[i] ? 4 : 0,
                  background: isToday ? 'hsl(var(--primary))' : 'hsl(var(--primary) / 0.35)',
                  borderRadius: 2,
                }}
              />
            </div>
            <div style={{ fontSize: 10, color: 'hsl(var(--muted-foreground))', marginTop: 4 }}>
              {counts[i]} job{counts[i] === 1 ? '' : 's'}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const today = new Date(NOW);
  const todayJobs = jobs
    .filter((j) => {
      const d = new Date(j.scheduledStart);
      return d.toDateString() === today.toDateString();
    })
    .sort((a, b) => new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime());

  const overdueInvoices = invoices.filter((i) => i.status === 'Overdue');
  const lowStockParts = parts.filter((p) => p.stock <= p.threshold);
  const completedThisWeek = jobs.filter((j) => j.status === 'Completed').length;
  const unpaidTotal = invoices
    .filter((i) => i.status !== 'Paid')
    .reduce((s, i) => s + (i.total - i.paid), 0);
  const revenueThisWeek = invoices
    .filter((i) => i.status === 'Paid')
    .reduce((s, i) => s + i.paid, 0);

  const techById = Object.fromEntries(techs.map((t) => [t.id, t]));
  const clientById = Object.fromEntries(clients.map((c) => [c.id, c]));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Good morning, Lin</h1>
          <p>Thursday, April 30 · {todayJobs.length} jobs scheduled today</p>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => navigate('/clients')}>
            {Icon.plus({ size: 14 })} New client
          </button>
          <button className="btn" onClick={() => navigate('/jobs')}>
            {Icon.plus({ size: 14 })} New estimate
          </button>
          <button className="btn primary" onClick={() => navigate('/jobs')}>
            {Icon.plus({ size: 14 })} New job
          </button>
        </div>
      </div>

      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <KPI
          label="Revenue this week"
          value={fmtMoney(revenueThisWeek)}
          delta="12.4%"
          deltaPositive={true}
          spark={[420, 510, 380, 620, 540, 480, 568]}
        />
        <KPI
          label="Jobs completed"
          value={completedThisWeek}
          delta="2"
          deltaPositive={true}
          spark={[2, 3, 1, 4, 2, 3, 4]}
        />
        <KPI
          label="Unpaid total"
          value={fmtMoney(unpaidTotal)}
          delta="$238"
          deltaPositive={false}
          spark={[200, 350, 300, 450, 480, 449, 449]}
        />
        <KPI
          label="Avg job time"
          value="1h 42m"
          delta="6 min"
          deltaPositive={true}
          spark={[110, 120, 105, 95, 100, 98, 102]}
        />
      </div>

      <div className="grid-2">
        <div className="stack gap-4">
          <div className="card">
            <div className="card-title">
              <span>Today's jobs</span>
              <button className="btn ghost sm" onClick={() => navigate('/jobs')}>
                View all {Icon.chevronRight({ size: 12 })}
              </button>
            </div>
            <div className="row-list">
              {todayJobs.length === 0 && <div className="empty-state">No jobs scheduled today.</div>}
              {todayJobs.map((j) => {
                const c = clientById[j.clientId]!;
                const addr = c.addresses[j.addressIdx]!;
                const tech = techById[j.techId];
                return (
                  <div className="row" key={j.id} onClick={() => navigate('/jobs/' + j.id)}>
                    <div className="row-time">{fmtTime(j.scheduledStart)}</div>
                    <div className="row-main">
                      <div className="row-title">{c.name}</div>
                      <div className="row-sub">
                        <span className="mono" style={{ fontSize: 11 }}>
                          {j.id}
                        </span>
                        {' · '}
                        {j.appliance.type} · {addr.line1}, {addr.city}
                      </div>
                    </div>
                    <div className="row-aside">
                      <StatusPill status={j.status} />
                      <TechAvatar tech={tech} size="sm" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card">
            <div className="card-title">
              <span>Upcoming · next 7 days</span>
              <button className="btn ghost sm" onClick={() => navigate('/calendar')}>
                Open calendar {Icon.chevronRight({ size: 12 })}
              </button>
            </div>
            <MiniWeek jobs={jobs} />
          </div>
        </div>

        <div className="stack gap-4">
          <div className="card">
            <div className="card-title">
              <span>Alerts</span>
              <span className="muted">
                {overdueInvoices.length + lowStockParts.length + 1} active
              </span>
            </div>
            <div className="alerts-list">
              <div className="alert-item warn">
                <div className="alert-icon">{Icon.phone({ size: 14 })}</div>
                <div className="alert-body">
                  <div className="alert-title">2 missed calls this morning</div>
                  <div className="alert-meta">Last from (214) 555-0188 · 8:42am</div>
                </div>
              </div>
              {overdueInvoices.map((i) => {
                const c = clientById[i.clientId]!;
                return (
                  <div className="alert-item error" key={i.id}>
                    <div className="alert-icon">{Icon.alert({ size: 14 })}</div>
                    <div className="alert-body">
                      <div className="alert-title">
                        Invoice {i.id} overdue · {fmtMoney(i.total)}
                      </div>
                      <div className="alert-meta">
                        {c.name} · due {fmtDate(i.due)} · 7 days late
                      </div>
                    </div>
                  </div>
                );
              })}
              {lowStockParts.slice(0, 3).map((p) => (
                <div className="alert-item info" key={p.id}>
                  <div className="alert-icon">{Icon.package({ size: 14 })}</div>
                  <div className="alert-body">
                    <div className="alert-title">Low stock · {p.name}</div>
                    <div className="alert-meta">
                      <span className="mono">{p.sku}</span> · {p.stock} on hand (reorder at{' '}
                      {p.threshold})
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-title">
              <span>Quick actions</span>
            </div>
            <div className="stack gap-2">
              <button
                className="btn"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate('/jobs')}
              >
                {Icon.plus({ size: 14 })} New job
              </button>
              <button
                className="btn"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate('/clients')}
              >
                {Icon.plus({ size: 14 })} New client
              </button>
              <button
                className="btn"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate('/jobs')}
              >
                {Icon.fileText({ size: 14 })} New estimate
              </button>
              <button
                className="btn"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate('/calendar')}
              >
                {Icon.calendar({ size: 14 })} Open calendar
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-title">
              <span>Today's tech load</span>
            </div>
            <div className="stack gap-3">
              {techs.map((t) => {
                const count = todayJobs.filter((j) => j.techId === t.id).length;
                return (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <TechAvatar tech={t} size="sm" />
                    <div style={{ flex: 1, fontSize: 13 }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                      {count} job{count === 1 ? '' : 's'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
